import {
    Box,
    Grid,
    Skeleton,
    Theme,
    Typography,
    useTheme,
} from "@mui/material";
import React, { Fragment } from 'react';
import { checkImageExists, isModuleAllowed, toINR } from "../../../../utils/utilityFunctions";
import { BlackDownArrowIcon, FailureIcon, GreenTickContainedIcon, InProgressIcon } from "../../../../assets/SvgIcons";
import { useHistory } from "react-router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getIndividualOrder } from "../../../../redux-store/actions/funds";
import BreadCrum from "../../../../lib/BreadCrum";
import { RootStateType } from "../../../../redux-store/reducers";
import { navigationData } from "../../types";
import { colorMap } from ".";
import { USER_ROLES } from "../../../../utils/constant";


interface InfoContainerProps {
    label: string;
    value: string | number;
    labelStyles?: React.CSSProperties;
    valueStyles?: React.CSSProperties;
    containerStyles?: React.CSSProperties;
    isLoading?: boolean;
}

const InfoContainer = (
    {
        label,
        value,
        labelStyles = {},
        valueStyles = {},
        containerStyles = {},
        isLoading = false
    }: InfoContainerProps
) => {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "4px",
                ...containerStyles
            }}
        >
            {isLoading && (
                <>
                    <Skeleton
                        sx={{
                            fontSize: "16px",
                            display: "inline-block"
                        }}
                        animation="wave"
                        width={80}
                    />
                    <Skeleton
                        sx={{
                            fontSize: "18px",
                            display: "inline-block"
                        }}
                        animation="wave"
                        width={80}
                    />
                </>
            )}
            {!isLoading && (
                <>
                    <Typography
                        variant="body_500"
                        sx={{
                            color: "text.secondary",
                            ...labelStyles
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        variant="body_600"
                        sx={{
                            color: "text.primary",
                            textWrap: 'nowrap',
                            fontSize: "16px",
                            ...valueStyles
                        }}
                    >
                        {value}
                    </Typography>
                </>)}
        </Box>
    )
}


const Loader = () => {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <Grid
            container
            columnSpacing={3}
            rowSpacing={3}
        >
            <Grid
                item
                xs={12}
                lg={8}
            >
                <Box
                    sx={(theme: Theme) => ({
                        borderRadius: "20px",
                        backgroundColor: theme.palette.common.white,
                        padding: "25px 30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <Skeleton
                        sx={{
                            fontSize: "20px",
                            display: "inline-block"
                        }}
                        animation="wave"
                        width={50}
                    />
                    <Box
                        sx={(theme: Theme) => ({
                            marginTop: "35px",
                            paddingBottom: "35px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start"
                        })}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "15px",
                                alignItems: "center"
                            }}
                        >
                            <Skeleton
                                variant="rounded"
                                width={50}
                                height={50}
                                sx={{
                                    borderRadius: "8px"
                                }}
                                animation="wave"
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    rowGap: "3px"
                                }}
                            >
                                <Skeleton
                                    sx={{
                                        fontSize: "18px",
                                        display: "inline-block"
                                    }}
                                    animation="wave"
                                    width={300}
                                />
                                <Box
                                    sx={{
                                        display: "flex",
                                        columnGap: "8px",
                                        alignItems: "center"
                                    }}
                                >
                                    <Skeleton
                                        sx={{
                                            fontSize: "16px",
                                            display: "inline-block"
                                        }}
                                        animation="wave"
                                        width={80}
                                    />
                                    <Skeleton
                                        variant="circular"
                                        width={3}
                                        height={3}
                                        sx={{
                                            borderRadius: "50%",
                                        }}
                                        animation="wave"
                                    />
                                    <Skeleton
                                        sx={{
                                            fontSize: "16px",
                                            display: "inline-block"
                                        }}
                                        animation="wave"
                                        width={80}
                                    />
                                    <Skeleton
                                        variant="circular"
                                        width={3}
                                        height={3}
                                        sx={{
                                            borderRadius: "50%",
                                        }}
                                        animation="wave"
                                    />
                                    <Skeleton
                                        sx={{
                                            fontSize: "16px",
                                            display: "inline-block"
                                        }}
                                        animation="wave"
                                        width={80}
                                    />
                                </Box>
                            </Box>
                        </Box >
                        <InfoContainer
                            label="Amount"
                            value={toINR({ value: 5000, locale, currencyCode })}
                            valueStyles={{
                                color: "primary.500",
                                fontSize: "18px"
                            }}
                            containerStyles={{
                                alignItems: "flex-end",
                                rowGap: "3px"
                            }}
                            isLoading={true}
                        />
                    </Box>
                    <Grid
                        container
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "35px",
                        }}
                    >
                        <Grid
                            item
                            xs={4}
                        >
                            <InfoContainer
                                label="Order Number"
                                value="98098912112"
                                containerStyles={{
                                    alignItems: "flex-start",
                                }}
                                isLoading={true}
                            />
                        </Grid>
                        <Grid
                            item
                            container
                            justifyContent="center"
                            xs={4}
                        >
                            <InfoContainer
                                label="Order Date"
                                value={toINR({ value: 67, locale, currencyCode })}
                                containerStyles={{
                                    alignItems: "flex-start",
                                }}
                                isLoading={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                        >
                            <InfoContainer
                                label="Frequency"
                                value="82.778"
                                containerStyles={{
                                    alignItems: "flex-end",
                                }}
                                isLoading={true}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "45px",
                            marginBottom: "20px",
                        }}
                    >
                        <Grid
                            item
                            xs={4}
                        >
                            <InfoContainer
                                label="No. of Installments"
                                value="12"
                                containerStyles={{
                                    alignItems: "flex-start",
                                }}
                                isLoading={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            container
                            justifyContent="center"
                        >
                            <InfoContainer
                                label="SIP Date"
                                value="26/10/23"
                                containerStyles={{
                                    alignItems: "flex-start",
                                }}
                                isLoading={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                        >
                            <InfoContainer
                                label="Mandate"
                                value="State Bank of India - XX.."
                                containerStyles={{
                                    alignItems: "flex-end",
                                }}
                                isLoading={true}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <Grid
                item
                xs={12}
                lg={4}
            >
                <Box
                    sx={(theme: Theme) => ({
                        borderRadius: "20px",
                        backgroundColor: theme.palette.common.white,
                        padding: "25px 30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <Box
                        sx={(theme: Theme) => ({
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingBottom: "35px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        })}
                    >
                        <Skeleton
                            sx={{
                                fontSize: '20px',
                                display: "inline-block"
                            }}
                            width={60}
                            animation="wave"
                        />
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "10px",
                                alignItems: "center"
                            }}
                        >
                            <Skeleton
                                variant="circular"
                                width={11}
                                height={11}
                                sx={{
                                    borderRadius: "50%"
                                }}
                                animation="wave"
                            />
                            <Skeleton
                                sx={{
                                    fontSize: '16px',
                                    display: "inline-block"
                                }}
                                width={60}
                                animation="wave"
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            marginTop: "33px"
                        }}
                    >
                        {Array.from({ length: 3 }).map((_, index, arr) => (
                            <Box
                                key={index}
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "flex-start",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative"
                                    }}
                                >
                                    <Skeleton
                                        variant="circular"
                                        width={20}
                                        height={20}
                                        sx={{
                                            borderRadius: "50%"
                                        }}
                                        animation="wave"
                                    />
                                    <Box sx={(theme: Theme) => ({
                                        top: "24px",
                                        bottom: 0,
                                        left: "9.5px",
                                        position: "absolute",
                                        borderLeft: `1px solid ${theme.palette.text.secondary}26`,
                                        display: index !== arr.length - 1 ? "block" : "none"
                                    })} />
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: "3px",
                                        marginLeft: "13px",
                                        paddingBottom: index !== arr.length - 1 ? "55px" : "0px"
                                    }}
                                >
                                    <Skeleton
                                        sx={{
                                            fontSize: '16px',
                                            display: "inline-block"
                                        }}
                                        width={100}
                                        animation="wave"
                                    />
                                    <Skeleton
                                        sx={{
                                            fontSize: '14px',
                                            display: "inline-block"
                                        }}
                                        width={60}
                                        animation="wave"
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Grid>
        </Grid >
    )
}


export default function OrderDetails(): JSX.Element {
    const theme = useTheme()

    const history = useHistory();
    const dispatch = useDispatch()
    const { pathname, state } = history.location;
    const statemanage = (state as { id?: string, clientCode?: string, tab?: string, indSchemeId?: number | string }) ?? ''
    const {
        role = '',
        permissions = []
    } = useSelector((store: RootStateType) => store.auth);
    const {
        selectedPortfolio
    } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const navState = state as { id?: string, clientCode?: string, }


    const { palette } = useTheme()

    const [details, setDetail] = useState<any>()
    const [isLoading, setLoading] = useState<boolean>(false)

    useEffect(() => {

        const fetchApi = async () => {

            setLoading(true)
            const payload = {
                // TransNo: statemanage.id as string,
                id: statemanage?.indSchemeId as string,
                portfolioId: selectedPortfolio?.value,
            }


            const res = (await dispatch(getIndividualOrder(payload))) as unknown;

            setDetail(res as any)
            setLoading(false)
        }

        fetchApi()
    }, [])

    // console.log("isLoading", isLoading)

    const initialRouter = pathname.split('/').filter((item) => item !== '')

    // console.log('path', initialRouter, pathName)

    const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            // warning: true
        },
        {
            name: initialRouter[1] + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode || ""
            }
        },
        {
            name: 'Mutual Funds',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode || "",
                tab: statemanage?.tab || ""
            },
            search: '?tab=Orders'
        },
        {
            name: 'Order Details',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: {
                clientCode: navState?.clientCode || ""
            }
        }
    ] : [])

    const investorRouterPath = [
        {
            name: 'Dashboard' + `${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
            // warning: true
        },
        {
            name: 'Mutual Funds',
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode || "",
                tab: statemanage?.tab || ""
            },
            search: '?tab=Orders'
        },
        {
            name: 'Order Details',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: {
                clientCode: navState?.clientCode || ""
            }
        }
    ]

    const findStepDataIcon = (status: string) => {
        switch (status) {
            case 'S':
                return <GreenTickContainedIcon />
            case 'P':
                return <InProgressIcon iconBgColor={theme.palette.warning[500]} />
            case 'F':
                return <FailureIcon />
            default:
                return <InProgressIcon iconBgColor={theme.palette.warning[500]} />
        }
    }

    return (
        <>
            <BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />
            {isLoading && <Loader />}
            {!isLoading && (
                <Grid
                    container
                    columnSpacing={3}
                    rowSpacing={3}
                    mt={'20px'}
                >
                    <Grid
                        item
                        xs={12}
                        lg={8}
                    >
                        <Box
                            sx={(theme: Theme) => ({
                                borderRadius: "20px",
                                backgroundColor: theme.palette.common.white,
                                padding: "25px 30px",
                                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                            })}
                        >
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({
                                    color: theme.palette.common.black
                                })}
                            >
                                {details?.transactionType}
                            </Typography>
                            {details?.folioNumber &&
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.common.black
                                    })}
                                >
                                    Folio no: {details?.folioNumber}
                                </Typography>
                            }


                            <Box
                                sx={(theme: Theme) => ({
                                    marginTop: "35px",
                                    paddingBottom: "35px",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start"
                                })}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: "15px" // Add spacing between each item
                                    }}
                                >
                                    {details?.schemes.map((scheme: any, i: number) => {
                                        const amcIds = ['BDOTR', 'BPIAMC']
                                        const checkAmcId = amcIds.includes(scheme?.amcId ?? '') ? scheme?.amcId?.toLowerCase() : scheme?.amcId;
                                        return (
                                            <Fragment key={i}>
                                                {i === 1 && (
                                                    <Box sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
                                                        <BlackDownArrowIcon />
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        columnGap: "15px",
                                                        alignItems: "center"
                                                    }}
                                                >
                                                    <img
                                                        src={checkImageExists(`${checkAmcId}`) ? `../../../../../../images/amcIcons/${checkAmcId}.svg` : `../../../../../../images/amcIcons/default.svg`}
                                                        alt={scheme?.name}
                                                        width={50}
                                                        height={50}
                                                        style={{
                                                            objectFit: "contain",
                                                            borderRadius: "8px"
                                                        }}
                                                    />
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            rowGap: "3px"
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body_600"
                                                            sx={(theme: Theme) => ({
                                                                color: theme.palette.text.primary,
                                                            })}
                                                        >
                                                            {scheme?.name}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                columnGap: "8px",
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body_500"
                                                                sx={(theme: Theme) => ({
                                                                    color: theme.palette.text.secondary
                                                                })}
                                                            >
                                                                {scheme?.type}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    width: "3px",
                                                                    height: "3px",
                                                                    backgroundColor: "text.secondary",
                                                                    alignSelf: "center",
                                                                    borderRadius: "50%",
                                                                    marginTop: "4px"
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body_500"
                                                                sx={(theme: Theme) => ({
                                                                    color: theme.palette.text.secondary
                                                                })}
                                                            >
                                                                {scheme?.cap}
                                                            </Typography>
                                                            {scheme?.risk !== null && <Box
                                                                sx={(theme: Theme) => ({
                                                                    width: "3px",
                                                                    height: "3px",
                                                                    backgroundColor: theme.palette.text.secondary,
                                                                    alignSelf: "center",
                                                                    borderRadius: "50%",
                                                                    marginTop: "4px"
                                                                })}
                                                            />}
                                                            {scheme?.risk !== null && <Typography
                                                                variant="body_500"
                                                                sx={(theme: Theme) => (
                                                                    { color: theme.palette.text.secondary }
                                                                )}
                                                            >
                                                                {scheme?.risk}
                                                            </Typography>}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Fragment>
                                        )
                                    })}
                                </Box>

                                <InfoContainer
                                    label="Amount / Units"
                                    value={`${toINR({ value: details?.amount, locale, currencyCode })} / ${details?.units ? Number(details?.units ?? "").toFixed(4) : 0}`}
                                    valueStyles={{
                                        color: "primary.500",
                                        fontSize: "18px"
                                    }}
                                    containerStyles={{
                                        alignItems: "flex-end",
                                        rowGap: "3px"
                                    }}
                                />
                            </Box>


                            {details?.schemes?.length === 1 || details?.transactionType === "STP" ? <Grid
                                container
                                rowGap={3}
                                sx={{
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    marginTop: "35px",
                                }}
                            >
                                {Object.entries(details?.details || {})?.filter(([key]) => !(details?.details?.Frequency === 'Daily' && key === "No. of Installment"))
                                    ?.map(([key = '', value = '']: [any, any], i: number) => (
                                        <Grid
                                            item
                                            xs={4}
                                            key={i}
                                        >
                                            <InfoContainer
                                                label={key || '-'}
                                                value={value || '-'}
                                                containerStyles={{
                                                    alignItems: i % 3 === 2 ? "flex-end" : i % 3 === 1 ? "center" : "flex-start",
                                                }}
                                            />
                                        </Grid>
                                    ))}

                                {/* <Grid
                            item
                            container
                            justifyContent="center"
                            xs={4}
                        >
                            <InfoContainer
                                label="Order Date"
                                value={toINR({value: 67, locale, currencyCode})}
                                containerStyles={{
                                    alignItems: "flex-start",
                                }}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                        >
                            <InfoContainer
                                label="Frequency"
                                value="82.778"
                                containerStyles={{
                                    alignItems: "flex-end",
                                }}
                            />
                        </Grid> */}
                                {/* </Grid>
                    <Grid
                        container
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: "45px",
                            marginBottom: "20px",
                        }}
                    > */}
                                {/* <Grid
                            item
                            xs={4}
                        >
                            <InfoContainer
                                label="No. of Installments"
                                value="12"
                                containerStyles={{
                                    alignItems: "flex-start",
                                }}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                            container
                            justifyContent="center"
                        >
                            <InfoContainer
                                label="SIP Date"
                                value="26/10/23"
                                containerStyles={{
                                    alignItems: "flex-start",
                                }}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={4}
                        >
                            <InfoContainer
                                label="Mandate"
                                value="State Bank of India - XX.."
                                containerStyles={{
                                    alignItems: "flex-end",
                                }}
                            />
                        </Grid> */}
                            </Grid> :
                                <Grid
                                    container
                                    sx={{
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginTop: "35px",
                                    }}
                                >
                                    <Grid
                                        item
                                        xs={6}
                                    >
                                        <InfoContainer
                                            label={"Order Number"}
                                            value={details?.details?.['Order Number'] || '-'}

                                        />
                                    </Grid>

                                    <Grid
                                        item
                                        xs={6}
                                    >
                                        <InfoContainer
                                            label={"Order Date"}
                                            value={details?.details?.['Order Date'] || '-'}
                                            containerStyles={{
                                                alignItems: "flex-end",
                                            }}
                                        />
                                    </Grid>

                                </Grid>
                            }
                            <Box sx={{ mt: 1 }}>
                                <Typography
                                    variant="body_500"
                                    sx={{
                                        color: "text.secondary",
                                        mb: '4px'
                                    }}
                                >Remarks</Typography>
                                {details?.bseResponse &&
                                    <Typography
                                        variant="subtitle_600"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary,
                                            fontWeight: 500
                                        })}
                                    >
                                        {details?.bseResponse ?? '-'}
                                    </Typography>
                                }
                                {details?.remarks &&
                                    <Typography
                                        variant="subtitle_600"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary,
                                            fontWeight: 500
                                        })}
                                    >
                                        {details?.remarks ?? '-'}
                                    </Typography>
                                }
                            </Box>
                        </Box>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        lg={4}
                    >
                        <Box
                            sx={(theme: Theme) => ({
                                borderRadius: "20px",
                                backgroundColor: theme.palette.common.white,
                                padding: "25px 30px",
                                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                            })}
                        >
                            <Box
                                sx={(theme: Theme) => ({
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    paddingBottom: "35px",
                                    borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                                })}
                            >
                                <Typography
                                    variant="h2"
                                    sx={{
                                        color: "text.primary"
                                    }}
                                >
                                    Status
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        columnGap: "10px",
                                        alignItems: "center"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderRadius: "50%",
                                            width: "11px",
                                            height: "11px",
                                            backgroundColor: colorMap[details?.paymentStatus?.status]
                                        }}
                                    />
                                    <Typography
                                        variant="body_500"
                                        sx={{
                                            color: "text.primary"
                                        }}
                                    >
                                        {details?.paymentStatus?.status}
                                    </Typography>

                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    marginTop: "33px"
                                }}
                            >
                                {details?.paymentStatus?.stepperData?.map((stepData: any, index: number, arr: any[]) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "flex-start",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: "relative"
                                            }}
                                        >
                                            {findStepDataIcon(`${stepData?.status ?? ""}`.toUpperCase())}
                                            <Box sx={(theme: Theme) => ({
                                                top: "23px",
                                                bottom: 0,
                                                left: "11px",
                                                position: "absolute",
                                                borderLeft: `1px solid ${theme.palette.text.secondary}26`,
                                                display: index !== arr.length - 1 ? "block" : "none"
                                            })} />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                rowGap: "3px",
                                                marginLeft: "13px",
                                                paddingBottom: index !== arr.length - 1 ? "55px" : "0px"
                                            }}
                                        >
                                            <Typography
                                                variant="body_500"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.text.primary
                                                })}
                                            >
                                                {stepData?.label}
                                            </Typography>
                                            <Typography
                                                variant="subtitle_500"
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.text.secondary
                                                })}
                                            >
                                                {stepData?.timeStamp}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                </Grid >
            )}
        </>
    )
}